import {
  EntityDiagramGraph,
  EntityDiagramVariant,
} from '@/modules/entityDiagram/types';
import { AfterDeath, TileNotesInput } from '@/types/schema';

import { entityNodeToTileNotesInput } from './types';

/**
 * Builds TileNotesInput entries for tiles on a single-entity/ownership
 * diagram. Mirrors buildTileNotesInput for the waterfall: tiles that cannot
 * generate notes (individuals, organizations, unsupported entity kinds) are
 * silently skipped, so callers can treat an empty result as "nothing to
 * generate for".
 */
export function buildEntityDiagramTileNotesInput(
  tileIds: string[],
  entityDiagramGraph: EntityDiagramGraph,
  entityDiagramVariant: EntityDiagramVariant
): TileNotesInput[] {
  const inputs: TileNotesInput[] = [];

  for (const tileId of tileIds) {
    let nodeAttrs;
    try {
      nodeAttrs = entityDiagramGraph.getNodeAttributes(tileId);
    } catch {
      // graphology throws when the node doesn't exist in the graph
      continue;
    }

    const data = nodeAttrs?.data;
    // Pixel-spacer tiles carry `node: null` and can't generate notes
    if (!data?.node) continue;

    // Only the dispositions variant sections tiles by actual grantor-death
    // events. The ownership and term variants reuse afterDeath as a layout
    // row (e.g. owners sit in the First row, term-end recipients in First =
    // "after term end"), so forwarding it would mislead the note generator —
    // those diagrams describe the current state.
    const deathSection =
      entityDiagramVariant === EntityDiagramVariant.Dispositions
        ? data.afterDeath
        : AfterDeath.None;

    const input = entityNodeToTileNotesInput(data.node, tileId, deathSection);
    if (input) {
      inputs.push(input);
    }
  }

  return inputs;
}
